import { CircleHelpIcon } from 'lucide-react'

import { Button } from '@/components/button/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/card/card'
import { Combobox } from '@/components/combobox/combobox'
import { DatePicker } from '@/components/date-picker/date-picker'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/dialog/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/select/select'

import { cn } from '@/lib/utils'

import { useUpdateProcessingTimeAlertDialog } from '../hooks/use-update-processing-time-alert-dialog'
import { ServiceCenterExplanationDialog } from './service-center-explanation-dialog'

export const UpdateProcessingTimeAlertDialog = () => {
  const {
    form,
    caseType,
    dataUscisFormCategories,
    serviceCenters,
    isOpenUpdateCaseInfoDialog,
    isOpenServiceCenterExplanationDialog,
    isPendingUpdateCaseExtraInfo,
    isDisabledGetProcessingTimeButton,
    openServiceCenterExplanationDialog,
    closeServiceCenterExplanationDialog,
    handleSubmitForm,
    handleChangeDialogOpen,
  } = useUpdateProcessingTimeAlertDialog()

  const existsDataUscisFormCategories = !!dataUscisFormCategories && dataUscisFormCategories.length > 0
  const existsServiceCenterData = !!serviceCenters && serviceCenters.length > 0

  return (
    <>
      <Dialog open={isOpenUpdateCaseInfoDialog} onOpenChange={handleChangeDialogOpen}>
        <DialogContent className={cn('max-w-[550px]')}>
          {/* Only for a11y */}
          <DialogHeader>
            <DialogTitle>Set Processing Time Alert</DialogTitle>
            <DialogDescription hidden>
              Select your form category, and the office that is processing your case.
            </DialogDescription>
          </DialogHeader>
          {/* Card content */}
          <Card className={cn('mt-[30px]', 'w-full', 'overflow-x-auto')}>
            <CardHeader>
              <CardDescription>Select your form category, and the office that is processing your case.</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmitForm)}>
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{caseType} Form Category*</FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger disabled={!existsDataUscisFormCategories}>
                              <SelectValue
                                placeholder={
                                  existsDataUscisFormCategories
                                    ? 'Select a Category'
                                    : 'No category available for this case type'
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {dataUscisFormCategories?.map(({ code, name }) => (
                                <SelectItem key={code} value={code}>
                                  {name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="serviceLocation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={cn('flex items-center space-x-2')}>
                          <span>Field Office or Service Center*</span>
                          <button type="button" onClick={openServiceCenterExplanationDialog}>
                            <CircleHelpIcon size={20} fill="#ABAFC9" className={cn('text-white')} />
                          </button>
                        </FormLabel>
                        <FormControl>
                          <Combobox
                            modal
                            items={serviceCenters.map(({ code, name }) => ({
                              label: name,
                              value: code,
                            }))}
                            placeholder={
                              existsServiceCenterData
                                ? 'Select One'
                                : 'No field office of service center available for this case type'
                            }
                            disabled={!existsServiceCenterData}
                            value={field.value}
                            onValueChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="receiptDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date filed*</FormLabel>
                        <DatePicker
                          value={field.value}
                          formatString="MM-DD-YYYY"
                          placeholder="MM-DD-YYYY"
                          onSelect={field.onChange}
                        />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    loading={isPendingUpdateCaseExtraInfo}
                    disabled={isDisabledGetProcessingTimeButton}
                  >
                    Set alert
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>

      <ServiceCenterExplanationDialog
        isOpen={isOpenServiceCenterExplanationDialog}
        close={closeServiceCenterExplanationDialog}
      />
    </>
  )
}
